/**
 * 首页快捷添加工具弹窗
 *
 * 两段式流程：
 *  1. 输入：用户粘贴 URL / 文本 / 简介，点「AI 识别」
 *  2. 预览：展示 AI 拆解出的 name / description / tags / links，用户可二次编辑后保存
 */
import React, { useEffect, useState } from 'react'
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Steps,
  Tag,
  Tooltip,
  message,
} from 'antd'
import {
  ArrowLeftOutlined,
  LinkOutlined,
  PlusOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import { api } from '../services/api'

const { TextArea } = Input

interface AddToolModalProps {
  visible: boolean
  onCancel: () => void
  onSuccess?: () => void
}

interface AnalyzedLink {
  url: string
  type?: string
  description?: string
}

interface AnalyzedTool {
  name: string
  description: string
  tags: string[]
  links: AnalyzedLink[]
}

const SAMPLE_HINT = `示例：
- 粘贴官网简介、产品页面、README 段落
- 直接粘贴一两个链接（URL 会被自动抽取）
- 写一段你自己对这个工具的理解，AI 会帮你补全`

const AddToolModal: React.FC<AddToolModalProps> = ({ visible, onCancel, onSuccess }) => {
  const [step, setStep] = useState<0 | 1>(0)
  const [inputText, setInputText] = useState('')
  const [analyzing, setAnalyzing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [analysis, setAnalysis] = useState<AnalyzedTool | null>(null)
  const [knownTags, setKnownTags] = useState<Array<{ id: number; name: string; color?: string }>>([])
  const [form] = Form.useForm()

  useEffect(() => {
    if (!visible) {
      // 重置弹窗状态
      setStep(0)
      setInputText('')
      setAnalysis(null)
      form.resetFields()
    } else {
      // 预加载已有标签，便于联想
      api.tags
        .list({ page: 1, page_size: 200 })
        .then((res: any) => {
          if (res?.success) setKnownTags(res.data?.items || [])
        })
        .catch(() => undefined)
    }
  }, [visible, form])

  const handleAnalyze = async () => {
    const text = inputText.trim()
    if (text.length < 10) {
      message.warning('至少输入 10 个字符，AI 才能识别')
      return
    }
    try {
      setAnalyzing(true)
      const res: any = await api.tools.analyzeText({ text })
      if (res.success && res.data?.analysis) {
        const a = res.data.analysis as AnalyzedTool
        setAnalysis({
          name: a.name || '未命名工具',
          description: a.description || '',
          tags: Array.isArray(a.tags) ? a.tags : [],
          links: Array.isArray(a.links) ? a.links : [],
        })
        form.setFieldsValue({
          name: a.name || '未命名工具',
          description: a.description || '',
          tags: Array.isArray(a.tags) ? a.tags : [],
          links: (Array.isArray(a.links) ? a.links : [])
            .map(l => l.url)
            .filter(Boolean)
            .join('\n'),
        })
        setStep(1)
      } else {
        message.error(res.message || '识别失败')
      }
    } catch (e: any) {
      console.error('AI 识别失败', e)
      const status = e?.response?.status
      if (status === 401) {
        message.warning('登录已失效，请重新登录后再试')
      } else {
        message.error(e?.response?.data?.detail || '识别失败，请检查网络或 AI 配置')
      }
    } finally {
      setAnalyzing(false)
    }
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      const linkLines = (values.links || '')
        .split('\n')
        .map((s: string) => s.trim())
        .filter(Boolean)
      const links = linkLines.map((url: string) => ({
        url,
        type: '相关链接',
        description: '',
      }))
      setSaving(true)
      const res: any = await api.tools.create({
        name: values.name,
        description: values.description || '',
        tags: values.tags || [],
        links,
      })
      if (res.success) {
        message.success('工具已添加')
        onSuccess?.()
        onCancel()
      } else {
        message.error(res.message || '保存失败')
      }
    } catch (e: any) {
      if (e?.errorFields) return // 表单校验
      console.error('保存工具失败', e)
      const status = e?.response?.status
      if (status === 401) {
        message.warning('登录已失效，请重新登录后再试')
      } else {
        message.error('保存失败')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={720}
      destroyOnClose
      title={
        <Space>
          <ToolOutlined />
          <span>添加工具（AI 自动识别）</span>
        </Space>
      }
    >
      <Steps
        size="small"
        current={step}
        items={[
          { title: '输入文本', icon: <ThunderboltOutlined /> },
          { title: '确认并保存', icon: <RobotOutlined /> },
        ]}
        style={{ marginBottom: 16 }}
      />

      {step === 0 && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <TextArea
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            placeholder={SAMPLE_HINT}
            autoSize={{ minRows: 6, maxRows: 12 }}
            maxLength={5000}
            showCount
          />
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>取消</Button>
            <Button
              type="primary"
              icon={<RobotOutlined />}
              loading={analyzing}
              onClick={handleAnalyze}
              disabled={inputText.trim().length < 10}
            >
              AI 识别
            </Button>
          </Space>
        </Space>
      )}

      {step === 1 && (
        <Spin spinning={analyzing}>
          <Form form={form} layout="vertical">
            <Form.Item
              label="名称"
              name="name"
              rules={[{ required: true, message: '请输入工具名称' }]}
            >
              <Input placeholder="工具名称" />
            </Form.Item>
            <Form.Item label="描述" name="description">
              <TextArea placeholder="工具描述" rows={4} maxLength={2000} showCount />
            </Form.Item>
            <Form.Item label="标签" name="tags">
              <Select
                mode="tags"
                placeholder="选择或输入新标签"
                allowClear
                tokenSeparators={[',', ' ']}
              >
                {knownTags.map(t => (
                  <Select.Option key={t.id} value={t.name}>
                    <Tag color={t.color}>{t.name}</Tag>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="链接（每行一个URL）" name="links">
              <TextArea placeholder="https://..." rows={3} />
            </Form.Item>
            {analysis?.links && analysis.links.length > 0 && (
              <Form.Item label="AI 抽取到的链接">
                <Space size={[4, 4]} wrap>
                  {analysis.links.map((l, idx) => (
                    <Tooltip key={`${l.url}-${idx}`} title={l.description || l.url}>
                      <Tag icon={<LinkOutlined />} color="blue">
                        <a href={l.url} target="_blank" rel="noreferrer">
                          {l.type || '链接'}
                        </a>
                      </Tag>
                    </Tooltip>
                  ))}
                </Space>
              </Form.Item>
            )}
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={() => setStep(0)}>
                  返回修改
                </Button>
                <Button onClick={onCancel}>取消</Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  loading={saving}
                  onClick={handleSave}
                >
                  保存到工具库
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      )}
    </Modal>
  )
}

export default AddToolModal